import React, { useEffect, useState } from "react";
import styled from "styled-components";
import config from "../config/app.json";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { getContextPath, getIsPremiumTemplate } from "../helpers/utils";
import Link from "next/link";
import useCheckIsMobile from "../hooks/useCheckIsMobile";

const LazyImageComponent = dynamic(() => import("../components/LazyImage"));

const MainSectionWrapper = styled.section`
  padding: 50px 50px;
  min-height: 150px;
  @media (max-width: 425px) {
    padding: 15px 15px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  scroll-behavior: smooth;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Tag = styled.a`
  border-radius: 20px;
  border: 1px solid #efefef;
  max-height: 34px;
  height: 34px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0px 20px;
  font-size: 14px;
  font-family: "Lato";
  text-transform: capitalize;
  margin: 10px 20px 10px 0px;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  background-color: ${(props) => (props.isCurrentTag ? "#54536e" : "#fff")};
  color: ${(props) => (props.isCurrentTag ? "#fff" : "#000")};
  box-shadow: 0px 0px 6px 0px #9696961a;
  &:active {
    background-color: #54536e;
    color: #ffffff;
  }

  @media (max-width: 425px) {
    margin: 0px 10px 0px 0px;
    font-size: 12px;
  }
`;

const TemplateContainer = styled.div`
  margin-top: 20px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  align-items: stretch;
  border-radius: 4px;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  grid-auto-flow: dense;
  > a {
    div {
      margin: 0px;
      min-width: 120px;
    }
  }

  @media (max-width: 425px) {
    gap: 10px;
  }

  @media (min-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const GridItem = styled.a`
  margin: 5px 0px;
  text-decoration: none;
  color: #54536e;
`;

const GridBlock = styled.div`
  display: block;
  gap: 10px;
  align-items: stretch;
  border-radius: 4px;
  columns: 5 auto;
  > a {
    div {
      margin: 0px;
      /* margin-bottom: 10px; */
      min-width: 120px;
      &:hover {
        transform: scale(0.95);
        transition: all 0.1s ease-in-out;
      }
    }
  }
  @media (max-width: 992px) {
    columns: 4 auto;
  }

  @media (max-width: 767px) {
    columns: 3 auto;
  }
  @media (max-width: 425px) {
    columns: 2 auto;
  }
`;

const TagsWrapper = styled.div`
  display: flex;
  position: relative;
`;

const ListSliderIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  cursor: pointer;
  z-index: 1;
  width: 50px;
  height: 50px;
  top: 53%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
`;

const LeftSliderIcon = styled(ListSliderIcon)`
  left: -35px;
  display: none;
`;

const RightSliderIcon = styled(ListSliderIcon)`
  right: -35px;
  ${(props) => !props.toShowRightSlider && `display: none !important`};
  @media (max-width: 425px) {
    display: none !important;
  }
`;

const TemplateName = styled.p`
  margin: ${(props) => (props.isGridBlock ? "15px 0px" : "15px 0px")};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const GridColumn = styled.div`
  display: grid;
  border-radius: 4px;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  column-gap: 24px;
  > div > a {
    div {
      @media (max-width: 600px) {
        min-width: 120px;
      }
    }
  }

  @media (min-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const appUrl = config["APPURLS"][config["APPURLS"]["current"]];
const apiUrl = config["APIURIS"][config["APIURIS"]["current"]];

function MainSection({
  data,
  tags,
  isSearchPage,
  toShowMessage,
  categoryName,
}) {
  const router = useRouter();
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTagName, setCurrentTagName] = useState(
    router.query.tagId ? router.query.tagId : ""
  );
  const { isMobileWidth, size } = useCheckIsMobile();
  const [gridSize, setGridSize] = useState(6);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (size[0] <= 425) {
      setGridSize(2);
    } else if (size[0] >= 1440) {
      setGridSize(6);
    } else if (size[0] >= 1024) {
      setGridSize(4);
    } else if (size[0] >= 768) {
      setGridSize(3);
    }
  }, [size]);

  function handleTagClick(event) {
    setCurrentTagName(event.target.id);
  }

  function handleLeftClick(event) {
    const element = event.target.parentElement.nextSibling.nextSibling;
    let offsetHeight = element.offsetWidth;
    element.scrollLeft -= offsetHeight;
  }

  function handleRightClick(event) {
    const element = event.target.parentElement.nextSibling;
    let offsetHeight = element.offsetWidth;
    element.scrollLeft += offsetHeight;
  }

  function handleScroll(event) {
    const element = event.target;
    let topVal = element.scrollLeft;
    let totalHeight = element.scrollWidth;
    let offsetHeight = element.offsetWidth;

    if (offsetHeight > 425) {
      if (topVal + offsetHeight >= totalHeight) {
        element.parentElement.children[1].style.display = "none";
      } else if (topVal === 0) {
        element.parentElement.children[0].style.display = "none";
        element.parentElement.children[1].style.display = "block";
      } else if (topVal > 0) {
        element.parentElement.children[0].style.display = "block";
        element.parentElement.children[1].style.display = "block";
      }
    }
  }

  function checkScrollWidth(id) {
    const element = document.getElementById(id);

    if (element.scrollWidth > window.innerWidth) {
      return true;
    }
    return false;
  }

  function transposeData(dataList) {
    let colIndex = 0;
    let result = [];
    for (let i = 0; i < dataList.length; i++) {
      if (result[colIndex] === undefined) {
        result.push([]);
      }
      result[colIndex].push(dataList[i]);
      colIndex++;
      if (colIndex > gridSize - 1) {
        colIndex = 0;
      }
    }

    return result;
  }

  return (
    <MainSectionWrapper>
      {!isSearchPage && (
        <TagsWrapper>
          <LeftSliderIcon onClick={handleLeftClick}>
            <img
              src={`${appUrl}static/icons/slide-left-arrow.svg`}
              width="50px"
              height="50px"
              title="left-click-icon"
              alt="left-click-icon"
            />
          </LeftSliderIcon>
          {isLoaded && (
            <RightSliderIcon
              onClick={handleRightClick}
              toShowRightSlider={checkScrollWidth("tag-container")}
            >
              <img
                src={`${appUrl}static/icons/slide-right-arrow.svg`}
                width="50px"
                height="50px"
                title="right-click-icon"
                alt="right-click-icon"
              />
            </RightSliderIcon>
          )}
          <TagsContainer onScroll={handleScroll} id="tag-container">
            {tags.map((tag, index) => (
              <Link
                key={index}
                href={`/${router.query.id}/${getContextPath(tag)}`}
                passHref
              >
                <Tag
                  id={tag}
                  isCurrentTag={
                    currentTagName.toLowerCase() ===
                    getContextPath(tag).toLowerCase()
                  }
                  onClick={handleTagClick}
                >
                  {tag}
                </Tag>
              </Link>
            ))}
          </TagsContainer>
        </TagsWrapper>
      )}
      <TemplateContainer>
        {!isSearchPage && !toShowMessage && data && data.length > 0 ? (
          <Grid>
            {data.map((item, index) => (
              <Link
                href={
                  router.query.tagId
                    ? router.query.continuation
                      ? `/[id]/[tagId]?id=${router.query.id}&tagId=${router.query.tagId}&templateId=${item["_source"].template_url}&continuation=${router.query.continuation}&origin=tag-page&contextId=${router.query.id}&contextTagId=${router.query.tagId}`
                      : `/[id]/[tagId]?id=${router.query.id}&tagId=${router.query.tagId}&templateId=${item["_source"].template_url}&origin=tag-page&contextId=${router.query.id}&contextTagId=${router.query.tagId}`
                    : router.query.continuation
                    ? `/[id]/?id=${router.query.id}&templateId=${item["_source"].template_url}&continuation=${router.query.continuation}&origin=template-page`
                    : `/[id]/?id=${router.query.id}&templateId=${item["_source"].template_url}&origin=template-page`
                }
                as={`/p/${item["_source"].template_url}`}
                key={index}
                passHref
                shallow
              >
                <GridItem type={item["_source"].picture_type}>
                  <LazyImageComponent
                    key={index}
                    src={`${appUrl}scene-prebuilts/thumbnails/${item["_source"].thumb}`}
                    alt={`${getContextPath(
                      item["_source"].name_en
                    )}-thumbnail-img`}
                    width={item["_source"].width}
                    height={item["_source"].height}
                    category={categoryName}
                    display_size={item["_source"].display_size}
                    isPremium={getIsPremiumTemplate(
                      item["_source"]["plan"],
                      item["_source"]["type"],
                      item["_source"]["subtype"]
                        ? item["_source"]["subtype"]
                        : item["_source"]["subType"]
                    )}
                  />
                  <TemplateName>{item["_source"].name_en}</TemplateName>
                </GridItem>
              </Link>
            ))}
          </Grid>
        ) : (
          <GridColumn>
            {transposeData(data, Math.ceil(data.length / gridSize)).map(
              (userChunk, index) => (
                <div className="columns" key={index}>
                  {userChunk.map((item, index) => (
                    <Link
                      href={
                        router.query.tagId
                          ? router.query.continuation
                            ? `/[id]/[tagId]?id=${router.query.id}&tagId=${router.query.tagId}&templateId=${item["_source"].template_url}&continuation=${router.query.continuation}&contextId=${router.query.id}&contextTagId=${router.query.tagId}`
                            : `/[id]/[tagId]?id=${router.query.id}&tagId=${router.query.tagId}&templateId=${item["_source"].template_url}&contextId=${router.query.id}&contextTagId=${router.query.tagId}`
                          : router.query.continuation
                          ? `${router.pathname}/?id=${router.query.id}&templateId=${item["_source"].template_url}&continuation=${router.query.continuation}`
                          : `${router.pathname}/?id=${router.query.id}&templateId=${item["_source"].template_url}`
                      }
                      key={index}
                      as={`/p/${item["_source"].template_url}`}
                      passHref
                      shallow
                    >
                      <GridItem type={item["_source"].picture_type}>
                        <LazyImageComponent
                          key={index}
                          src={`${appUrl}scene-prebuilts/thumbnails/${item["_source"].thumb}`}
                          alt={`${getContextPath(
                            item["_source"].name_en
                          )}-thumbnail-img`}
                          width={item["_source"].width}
                          height={item["_source"].height}
                          display_size={item["_source"].display_size}
                          category={item["_source"].category_name}
                          isPremium={getIsPremiumTemplate(
                            item["_source"]["plan"],
                            item["_source"]["type"],
                            item["_source"]["subtype"]
                              ? item["_source"]["subtype"]
                              : item["_source"]["subType"]
                          )}
                        />
                        <TemplateName isGridBlock={true}>
                          {item["_source"].name_en}
                        </TemplateName>
                      </GridItem>
                    </Link>
                  ))}
                </div>
              )
            )}
          </GridColumn>
        )}
      </TemplateContainer>
    </MainSectionWrapper>
  );
}

export default React.memo(MainSection);
